import { MAX_RECURRENCE_OCCURRENCES, type RecurrenceRule, type WeekdayCode } from '@streep/types'

export interface OccurrenceTemplate {
  startTijd: Date
  eindTijd: Date
  isAllDay: boolean
}
export interface GeneratedOccurrence {
  start: Date
  end: Date
}

const WEEKDAY_TO_DOW: Record<WeekdayCode, number> = {
  SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6,
}
const MAX_ITERS = 100_000

function atSameTime(template: Date, y: number, m: number, d: number): Date {
  return new Date(
    Date.UTC(
      y, m, d,
      template.getUTCHours(), template.getUTCMinutes(),
      template.getUTCSeconds(), template.getUTCMilliseconds(),
    ),
  )
}
function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
}

/**
 * Genereert alle voorkomens (incl. de eerste = template.startTijd), in
 * chronologische volgorde.
 *
 * `cap` is de harde bovengrens op het aantal voorkomens (default
 * `MAX_RECURRENCE_OCCURRENCES`). De API roept dit aan met `MAX + 1` zodat een
 * legitieme `COUNT: 400` (of UNTIL die exact 400 oplevert) volledig
 * gegenereerd wordt en alleen een echt te-lange reeks (> MAX) wordt afgewezen.
 */
export function generateOccurrences(
  template: OccurrenceTemplate,
  rule: RecurrenceRule,
  cap = MAX_RECURRENCE_OCCURRENCES,
): GeneratedOccurrence[] {
  const duration = template.eindTijd.getTime() - template.startTijd.getTime()
  const anchor = template.startTijd
  const ay = anchor.getUTCFullYear()
  const am = anchor.getUTCMonth()
  const ad = anchor.getUTCDate()

  const untilTs =
    rule.end.type === 'UNTIL'
      ? Date.UTC(
          Number(rule.end.date.slice(0, 4)),
          Number(rule.end.date.slice(5, 7)) - 1,
          Number(rule.end.date.slice(8, 10)),
          23, 59, 59, 999,
        )
      : null
  const countTarget = rule.end.type === 'COUNT' ? rule.end.count : null

  const out: GeneratedOccurrence[] = []
  // returns false => stop generating
  const push = (start: Date): boolean => {
    if (untilTs !== null && start.getTime() > untilTs) return false
    out.push({ start, end: new Date(start.getTime() + duration) })
    if (countTarget !== null && out.length >= countTarget) return false
    if (out.length >= cap) return false
    return true
  }

  if (rule.freq === 'DAILY') {
    for (let i = 0; i < MAX_ITERS; i++) {
      if (!push(atSameTime(anchor, ay, am, ad + i * rule.interval))) break
    }
  } else if (rule.freq === 'WEEKLY') {
    const dows =
      rule.byWeekday && rule.byWeekday.length > 0
        ? rule.byWeekday.map((c) => WEEKDAY_TO_DOW[c])
        : [anchor.getUTCDay()]
    const dowSet = new Set(dows)
    const mondayOffset = (anchor.getUTCDay() + 6) % 7 // dagen sinds maandag
    const blockMondayDay = ad - mondayOffset
    let stop = false
    for (let block = 0; !stop && block < MAX_ITERS; block++) {
      const weekStart = block * 7 * rule.interval
      for (let dow = 0; dow < 7; dow++) {
        const isoDow = (dow + 1) % 7 // dow 0->Mon(1) .. 6->Sun(0)
        if (!dowSet.has(isoDow)) continue
        const start = atSameTime(anchor, ay, am, blockMondayDay + weekStart + dow)
        if (start.getTime() < anchor.getTime()) continue
        if (!push(start)) { stop = true; break }
      }
    }
  } else if (rule.freq === 'MONTHLY') {
    for (let i = 0; i < MAX_ITERS; i++) {
      const total = am + i * rule.interval
      const y = ay + Math.floor(total / 12)
      const m = ((total % 12) + 12) % 12
      if (ad > daysInMonth(y, m)) {
        if (untilTs !== null && Date.UTC(y, m, 1, 0, 0, 0, 0) > untilTs) break
        continue
      }
      if (!push(atSameTime(anchor, y, m, ad))) break
    }
  } else {
    // YEARLY
    for (let i = 0; i < MAX_ITERS; i++) {
      const y = ay + i * rule.interval
      if (ad > daysInMonth(y, am)) {
        if (untilTs !== null && Date.UTC(y, am, 1, 0, 0, 0, 0) > untilTs) break
        continue
      }
      if (!push(atSameTime(anchor, y, am, ad))) break
    }
  }

  return out
}
